import { createTheme } from '@mui/material/styles';

// MINIMALISM Color Palette - AppDataTable Header Blue + Gold Accent
const colors = {
  primary: {
    main: '#1e3c72', // AppDataTable header blue - primary actions
    light: '#2b5aa8', // Brighter blue for hover-adjacent states
    dark: '#152c54', // Deep blue for pressed states
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#d97706', // Gold accent - headers & highlights
    light: '#f59e0b', // Light gold for backgrounds
    dark: '#b45309', // Dark gold for pressed states
    contrastText: '#ffffff',
  },
  success: {
    main: '#065f46', // Forest green
    light: '#10b981',
    dark: '#047857',
  },
  error: {
    main: '#7f1d1d', // Burgundy red
    light: '#ef4444',
    dark: '#7f1d1d',
  },
  warning: {
    main: '#ffa000', // Amber
    light: '#ffb74d',
    dark: '#ff6f00',
  },
  info: {
    main: '#1e3c72', // Same family as primary
    light: '#2b5aa8',
    dark: '#152c54',
  },
  background: {
    default: '#f4f7fb', // Cool canvas that separates operational surfaces
    paper: '#ffffff', // Pure white for cards and surfaces
  },
  text: {
    primary: '#212121', // Nearly black (not pure black) - excellent contrast
    secondary: '#666666', // Medium gray for secondary text
  },
  divider: '#dfe6ef', // Light blue-gray dividers
};

// Create the minimalism theme
export const theme = createTheme({
  palette: {
    mode: 'light',
    ...colors,
  },
  typography: {
    fontFamily: [
      'Aptos Display',
      'Aptos',
      '"Segoe UI Variable"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.125rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: 0,
      color: '#d97706', // ACCENT GOLD for prominence
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: 0,
      color: '#d97706', // ACCENT GOLD for section headers
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: 0,
      color: colors.text.primary, // Dark gray for subsections
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: colors.text.primary,
    },
    h5: {
      fontSize: '1.05rem',
      fontWeight: 600,
      lineHeight: 1.5,
      color: colors.text.primary,
    },
    h6: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.6,
      color: colors.text.primary,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: colors.text.primary,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: colors.text.secondary,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.9375rem',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
      color: colors.text.secondary,
    },
  },
  shape: {
    borderRadius: 10,
  },
  // MINIMALISM: Subtle shadows only - Level 1-2 maximum
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',                    // Level 1: Subtle
    '0px 2px 4px rgba(0, 0, 0, 0.08)',                    // Level 2: Light hover
    '0px 4px 8px rgba(0, 0, 0, 0.10)',                    // Level 3: Medium
    '0px 8px 16px rgba(0, 0, 0, 0.12)',                   // Level 4: Large (modals)
    '0px 12px 24px rgba(0, 0, 0, 0.14)',                  // Level 5: Extra
    '0px 16px 32px rgba(0, 0, 0, 0.16)',                  // Level 6: Maximum
    'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none', 'none',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.default,
          color: colors.text.primary,
          fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        size: 'medium',
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          minWidth: 96,
          fontSize: '0.9375rem',
          fontWeight: 600,
          lineHeight: 1.35,
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)', // Level 1: Subtle shadow
          transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
          textTransform: 'none',
          border: 'none',
          '& .MuiButton-startIcon, & .MuiButton-endIcon': {
            '> *:nth-of-type(1)': {
              fontSize: '1.05rem',
            },
          },
          '&.Mui-disabled': {
            color: '#9ca3af',
            backgroundColor: '#e5e7eb',
            borderColor: '#d1d5db',
            boxShadow: 'none',
            transform: 'none',
          },
        },
        sizeSmall: {
          minHeight: 32,
          padding: '6px 12px',
          fontSize: '0.8125rem',
          borderRadius: 6,
        },
        sizeMedium: {
          minHeight: 36,
          padding: '8px 16px',
        },
        sizeLarge: {
          minHeight: 42,
          padding: '10px 20px',
          fontSize: '1rem',
        },
        containedPrimary: {
          backgroundColor: colors.primary.main,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: colors.primary.dark,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            backgroundColor: colors.primary.dark,
            boxShadow: 'none',
            transform: 'translateY(0)',
          },
        },
        containedSecondary: {
          backgroundColor: colors.secondary.main,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: colors.secondary.dark,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            backgroundColor: colors.secondary.dark,
            boxShadow: 'none',
            transform: 'translateY(0)',
          },
        },
        contained: {
          backgroundColor: colors.primary.main,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: colors.primary.dark,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            backgroundColor: colors.primary.dark,
            boxShadow: 'none',
            transform: 'translateY(0)',
          },
        },
        outlined: {
          backgroundColor: 'transparent',
          color: colors.primary.main,
          border: `1px solid ${colors.primary.main}`,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#f3f4f6',
            border: `1px solid ${colors.primary.main}`,
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
          },
        },
        outlinedPrimary: {
          color: colors.primary.main,
          border: `1px solid ${colors.primary.main}`,
          '&:hover': {
            backgroundColor: '#f3f4f6',
            border: `1px solid ${colors.primary.dark}`,
          },
        },
        outlinedSecondary: {
          color: colors.secondary.main,
          border: `1px solid ${colors.secondary.main}`,
          '&:hover': {
            backgroundColor: '#fff7ed',
            border: `1px solid ${colors.secondary.dark}`,
          },
        },
        text: {
          color: colors.primary.main,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#f3f4f6',
          },
        },
        textPrimary: {
          color: colors.primary.main,
          '&:hover': {
            backgroundColor: '#f3f4f6',
          },
        },
        textSecondary: {
          color: colors.secondary.main,
          '&:hover': {
            backgroundColor: '#fff7ed',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, transform 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: '#f3f4f6',
          },
        },
        colorPrimary: {
          color: colors.primary.main,
        },
        colorSecondary: {
          color: colors.secondary.main,
          '&:hover': {
            backgroundColor: '#fff7ed',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)', // Level 1: Subtle
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.divider}`,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)', // Level 2: Light hover
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: colors.background.paper,
          border: `1px solid ${colors.divider}`,
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)', // Level 1
        },
        elevation2: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)', // Level 2
        },
        elevation3: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)', // Keep standard level consistent across papers
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: colors.background.paper,
            transition: 'all 0.2s ease-in-out',
            '&.Mui-disabled': {
              backgroundColor: '#f3f4f6',
            },
            '& fieldset': {
              borderColor: colors.divider,
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: '#dddddd',
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              borderWidth: '2px',
              boxShadow: `0 0 0 3px ${colors.primary.main}20`,
            },
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: colors.background.paper,
            '&.Mui-disabled': {
              backgroundColor: '#f3f4f6',
            },
            '& fieldset': {
              borderColor: colors.divider,
            },
            '&:hover fieldset': {
              borderColor: '#dddddd',
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
              borderWidth: 2,
              boxShadow: `0 0 0 3px ${colors.primary.main}20`,
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          marginLeft: 0,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9375rem',
          minHeight: 48,
          transition: 'all 0.2s ease-in-out',
          color: colors.text.secondary,
          borderRadius: 6,
          margin: '0 4px',
          '&.Mui-selected': {
            color: colors.primary.main,
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: colors.primary.main,
            color: colors.primary.contrastText,
            fontWeight: 600,
            fontSize: '0.875rem',
            borderBottom: `1px solid ${colors.divider}`,
            borderTop: `1px solid ${colors.divider}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: colors.divider,
          padding: '10px 16px',
          backgroundColor: colors.background.paper,
        },
        head: {
          fontWeight: 600,
          backgroundColor: colors.primary.main,
          color: colors.primary.contrastText,
          borderBottom: `1px solid ${colors.divider}`,
        },
        body: {
          color: colors.text.primary,
          borderBottomColor: colors.divider,
          '&:first-of-type': {
            fontWeight: 500,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: '#fafafa', // Light gray hover
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 24,
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
          backgroundColor: colors.background.paper,
          color: colors.text.primary,
          border: `1px solid ${colors.divider}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          backgroundColor: colors.background.default,
          boxShadow: '2px 0 4px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${colors.divider}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: colors.background.paper,
          color: colors.text.primary,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          borderBottom: `1px solid ${colors.divider}`,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.9375rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          backgroundColor: colors.background.paper,
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)', // Level 4: Large shadow for modals
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '16px 20px 12px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 20px 16px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          margin: '2px 8px',
          transition: 'all 0.2s ease-in-out',
          color: colors.text.primary,
          '&:hover': {
            backgroundColor: '#f3f4f6', // Neutral hover
            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
          },
          '&.Mui-selected': {
            backgroundColor: '#e5e7eb',
            color: colors.primary.main,
            '&:hover': {
              backgroundColor: '#d1d5db',
            },
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.primary.main,
          textDecoration: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            color: colors.primary.dark,
            textDecoration: 'underline',
          },
        },
      },
    },
  },
});

export default theme;
